import { useEffect, useMemo, useState } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { Loader2, RotateCcw, Save, X as CloseIcon, ExternalLink } from "lucide-react";
import { IconToggleRight, IconToggleLeft } from "@tabler/icons-react";
import {
  Instagram,
  Youtube,
  Facebook,
  Link2,
  Twitter,
} from "react-feather";
import { DiscordIcon } from "@/assets/discord";
import TikTokIcon from "@/assets/tiktok.tsx";
import postcheckoutGuide from "@/assets/post-checkout-guide.gif";
import PageHeader from "@/components/common/PageHeader";

import {
  THANK_YOU_BANNER_LAYOUTS,
  ThankYouBannerConfig,
  ThankYouBannerLayout,
  ThankYouBannerManagementResponse,
  UpdateThankYouBannerPayload,
} from "@/types/brand";
import { thankYouBannerService } from "@/services/thankYouBanner.service";
import { useBrandAuth } from "@/contexts/BrandAuthContext/context";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { DialogRoot, DialogContent, DialogHeader, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { ImageUploader } from "@/components/ImageUploader/ImageUploader";
import { storageService } from "@/services/storage.service";
import { cn } from "@/lib/utils";

const QUERY_KEY = ["thank-you-banner"];

const getSocialIcon = (platformName: string) => {
  switch (platformName.toUpperCase()) {
    case "INSTAGRAM":
      return <Instagram className="w-5 h-5 text-muted-foreground" />;
    case "YOUTUBE":
      return <Youtube className="w-5 h-5 text-muted-foreground" />;
    case "FACEBOOK":
      return <Facebook className="w-5 h-5 text-muted-foreground" />;
    case "TIKTOK":
      return <TikTokIcon className="w-5 h-5" />;
    case "DISCORD":
      return <DiscordIcon className="w-5 h-5 text-muted-foreground" />;
    case "TWITTER":
    case "X":
      return <Twitter className="w-5 h-5 text-muted-foreground" />;
    case "OTHER":
      return <Link2 className="w-5 h-5 text-muted-foreground" />;
    default:
      return null;
  }
};

export default function PostCheckout() {
  const queryClient = useQueryClient();
  const { user } = useBrandAuth();
  const { data, isLoading, isFetching } =
    useQuery<ThankYouBannerManagementResponse>({
      queryKey: QUERY_KEY,
      queryFn: () => thankYouBannerService.getConfig(),
    });

  const [localConfig, setLocalConfig] = useState<ThankYouBannerConfig | null>(
    null
  );
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [isFirstSave, setIsFirstSave] = useState(false);

  useEffect(() => {
    if (data?.config) {
      // Only expose STYLE_1 and STYLE_3 in the UI.
      // If an existing config is STYLE_2, map it to STYLE_1 for the frontend.
      setLocalConfig({
        ...data.config,
        layoutStyle:
          data.config.layoutStyle === "STYLE_2"
            ? "STYLE_1"
            : data.config.layoutStyle,
      });
      // Check if this is a new configuration (no id means it was just created)
      if (!data.config.id) {
        setIsFirstSave(true);
      }
    }
  }, [data]);

  const mutation = useMutation({
    mutationFn: (payload: UpdateThankYouBannerPayload) =>
      thankYouBannerService.updateConfig(payload),
    onSuccess: (response) => {
      queryClient.setQueryData(QUERY_KEY, response);
      setLocalConfig(response.config);
      toast.success("Post-checkout settings updated");
      
      // Show setup modal on first save
      if (isFirstSave) {
        setShowSetupModal(true);
        setIsFirstSave(false);
      }
    },
    onError: () => {
      toast.error("Unable to update post-checkout settings");
    },
  });

  const hasChanges = useMemo(() => {
    if (!localConfig || !data?.config) return false;
    return JSON.stringify(localConfig) !== JSON.stringify(data.config);
  }, [localConfig, data?.config]);

  const selectedSocials = useMemo(() => {
    if (!localConfig || !data?.availableSocialMedia) return [];
    return data.availableSocialMedia.filter((social) =>
      localConfig.socialMediaIds.includes(social.id)
    );
  }, [localConfig, data?.availableSocialMedia]);

  const previewCommission = localConfig && localConfig.commissionValue
    ? localConfig.commissionType === "PERCENTAGE"
      ? `${localConfig.commissionValue}%`
      : `$${localConfig.commissionValue}`
    : "—";

  const previewDiscount = localConfig && localConfig.discountValue
    ? localConfig.discountType === "PERCENTAGE"
      ? `${localConfig.discountValue}%`
      : `$${localConfig.discountValue}`
    : "—";

  const headlinePreview = localConfig
    ? localConfig.headlineTemplate.replace(
        /\{\{\s*commissionPercentage\s*\}\}/gi,
        previewCommission
      )
    : "";

  const commissionMessagePreview =
    localConfig?.commissionMessage ??
    "Share your referral link and earn cash on every purchase you influence.";

  const handleFieldChange = <K extends keyof ThankYouBannerConfig>(
    key: K,
    value: ThankYouBannerConfig[K]
  ) => {
    setLocalConfig((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const toggleSocial = (id: string) => {
    setLocalConfig((prev) => {
      if (!prev) return prev;
      const exists = prev.socialMediaIds.includes(id);
      return {
        ...prev,
        socialMediaIds: exists
          ? prev.socialMediaIds.filter((socialId) => socialId !== id)
          : [...prev.socialMediaIds, id],
      };
    });
  };

  const handleLogoUpload = async (file: File) => {
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size should be less than 5MB");
      return;
    }

    setIsUploadingLogo(true);
    try {
      const imageUrl = await storageService.uploadThankYouBannerLogo(file);
      handleFieldChange("bannerLogo", imageUrl);
      toast.success("Logo uploaded successfully");
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload logo. Please try again.");
    } finally {
      setIsUploadingLogo(false);
    }
  };

  const handleSave = () => {
    if (!localConfig) return;
    
    const payload: UpdateThankYouBannerPayload = {
      bannerLogo: localConfig.bannerLogo,
      headlineTemplate: localConfig.headlineTemplate,
      includeCustomerName: localConfig.includeCustomerName,
      commissionMessage: localConfig.commissionMessage,
      layoutStyle: localConfig.layoutStyle,
      socialMediaIds: localConfig.socialMediaIds,
      isEnabled: localConfig.isEnabled,
      commissionType: localConfig.commissionType,
      commissionValue: localConfig.commissionValue,
      discountType: localConfig.discountType,
      discountValue: localConfig.discountValue,
    };
    mutation.mutate(payload);
  };


  const handleReset = () => {
    if (data?.config) {
      setLocalConfig({
        ...data.config,
        layoutStyle:
          data.config.layoutStyle === "STYLE_2"
            ? "STYLE_1"
            : data.config.layoutStyle,
      });
    }
  };

  const handleGetReferralLink = (code: string) => {
    if (!user?.shop) {
      toast.error("Shop information not available");
      return;
    }

    // Normalize shop domain
    let shopDomain = user.shop;
    if (!shopDomain.includes('.myshopify.com')) {
      shopDomain = `${shopDomain}.myshopify.com`;
    }

    // Create store URL with discount code
    const storeUrl = `https://${shopDomain}/discount/${code}`;
    
    // Open in new tab
    window.open(storeUrl, '_blank');
  };

  const renderPreviewContent = () => {
    if (!localConfig) return null;

    const logo = localConfig.bannerLogo ? (
      <img
        src={localConfig.bannerLogo}
        alt="Banner banner"
        className="w-full max-w-[640px] h-40 md:h-56 object-cover rounded-xl border"
      />
    ) : null;

    // Use a placeholder code for preview
    const previewCode = "EDEN3434";

    const ctaButton = (
      <button 
        onClick={() => handleGetReferralLink(previewCode)}
        className="w-fit rounded-md bg-[#0074F0] px-6 py-3 h-12 text-white font-medium shadow inline-flex items-center justify-center hover:bg-[#0060D0] transition-colors"
      >
        Get my referral link
      </button>
    );

    const referralCard = (
      <div className="rounded-xl border border-border bg-muted px-4 h-12 flex items-center text-left">
        <div>
          <p className="text-xs font-medium text-muted-foreground">
            Your referral code
          </p>
          <p className="text-base font-semibold text-muted-foreground tracking-wide leading-tight">
            EDEN3434
          </p>
        </div>
      </div>
    );

    const socialsBlock =
      selectedSocials.length > 0 ? (
        <div className="flex flex-wrap gap-4">
          {selectedSocials.map((social) => (
            <div
              key={social.id}
              className="flex items-center gap-2 text-sm text-muted-foreground"
            >
              {social.iconUrl ? (
                <img
                  src={social.iconUrl}
                  alt={social.platformName}
                  className="h-6 w-6 object-contain"
                  onError={(e) => {
                    const target = e.currentTarget;
                    target.style.display = "none";
                    const iconContainer = target.parentElement?.querySelector(
                      ".preview-social-icon-fallback"
                    ) as HTMLElement;
                    if (iconContainer) {
                      iconContainer.style.display = "flex";
                    }
                  }}
                />
              ) : null}
              <div
                className="preview-social-icon-fallback"
                style={{
                  display: social.iconUrl ? "none" : "flex",
                  alignItems: "center",
                }}
              >
                {getSocialIcon(social.platformName) || (
                  <div className="h-6 w-6 rounded-full bg-gray-200 flex items-center justify-center text-xs font-semibold">
                    {social.platformName.charAt(0)}
                  </div>
                )}
              </div>
              <span>@{social.username}</span>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">
          Select which social profiles to highlight.
        </p>
      );

    switch (localConfig.layoutStyle) {
      
      case "STYLE_3":
        // Minimal layout: standard left-aligned content
        // Referral code and button appear on the same row
        return (
          <div className="flex flex-col gap-4 max-w-[640px] mx-auto">
            {/* Optional logo (smaller visual weight than spotlight) */}
            {logo && <div className="max-w-[480px]">{logo}</div>}

            {/* Headline and message */}
            <div className="space-y-2">
              <p className="text-lg font-semibold text-foreground">
                {localConfig.includeCustomerName ? "Eden Michael, " : ""}
                {headlinePreview}
              </p>
              <p className="text-sm text-muted-foreground">{commissionMessagePreview}</p>
            </div>

            {/* Referral code + button inline on larger screens */}
            <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
              <div className="w-full sm:max-w-[220px]">{referralCard}</div>
              <div className="sm:flex-shrink-0">{ctaButton}</div>
            </div>

            {/* Social proof */}
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Follow us</p>
              {socialsBlock}
            </div>
          </div>
        );

      case "STYLE_1":
      default:
        // Spotlight layout: matches the Shopify thank-you card look
        // Large banner on top, headline, referral code card, CTA button, and socials
        return (
          <div className="flex flex-col gap-6 max-w-[640px] mx-auto">
            {/* Banner image */}
            {logo}

            {/* Headline and message */}
            <div className="space-y-2">
              <p className="text-xl font-semibold text-foreground">
                {localConfig.includeCustomerName ? "Eden Michael, " : ""}
                {headlinePreview}
              </p>
              <p className="text-base text-muted-foreground">
                {commissionMessagePreview}
              </p>
            </div>

            {/* Referral code card */}
            {referralCard}

            {/* CTA button */}
            <div>
              {ctaButton}
            </div>

            {/* Social proof */}
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">Follow us</p>
              {socialsBlock}
            </div>
          </div>
        );
    }
  };

  if (isLoading || !localConfig) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-brand-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4">
      <PageHeader
        title="Post-checkout affiliate onboarding"
        description="Turn your customers into affiliates! Show a banner on the Shopify thank-you page inviting buyers to become affiliates with custom commission and discount rates."
      />
      <div className="flex flex-col gap-4">

        {/* Feature Status Card */}
        <Card className="bg-transparent border-none shadow-none">
          <CardHeader className="relative">
            <CardDescription className="flex items-center gap-2">
              {localConfig.isEnabled ? (
                <IconToggleRight className="h-4 w-4 text-neutral-500" />
              ) : (
                <IconToggleLeft className="h-4 w-4 text-neutral-500" />
              )}
              Post-checkout Banner
            </CardDescription>
            <CardTitle className="text-2xl font-semibold tabular-nums">
              {localConfig.isEnabled ? "Active" : "Inactive"}
            </CardTitle>
            <div className="absolute right-4 top-4">
              <div className="flex items-center gap-3">
                <Switch
                  checked={localConfig.isEnabled}
                  onCheckedChange={(checked) =>
                    handleFieldChange("isEnabled", Boolean(checked))
                  }
                  className="data-[state=checked]:bg-brand-600"
                />
                <Badge 
                  variant="outline" 
                  className={cn(
                    "flex gap-1 rounded-lg text-xs",
                    localConfig.isEnabled 
                      ? "bg-green-50 text-green-700 border-green-200" 
                      : "bg-muted text-muted-foreground border-border"
                  )}
                >
                  {localConfig.isEnabled ? "Enabled" : "Disabled"}
                </Badge>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                {localConfig.isEnabled 
                  ? "Post-checkout banner is active on your thank-you page" 
                  : "Enable to show the banner on your thank-you page"}
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowSetupModal(true)}
                className="gap-2"
              >
                <ExternalLink className="w-4 h-4" />
                Setup Guide
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Save/Reset Buttons */}
        {hasChanges && (
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={handleReset}
              className="flex items-center gap-2"
            >
              <RotateCcw className="w-4 h-4" />
              Reset
            </Button>
            <Button
              onClick={handleSave}
              disabled={mutation.isPending}
            >
              {mutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              {mutation.isPending ? "Saving..." : "Save changes"}
            </Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="space-y-6">
          <CardHeader>
            <CardTitle>Commission & discount settings</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-900 font-medium">
                💡 These settings are specific to post-checkout affiliates and separate from your program settings.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="commissionType">Commission type</Label>
                <select
                  id="commissionType"
                  value={localConfig.commissionType}
                  onChange={(e) =>
                    handleFieldChange("commissionType", e.target.value)
                  }
                  className="w-full rounded-md border border-border p-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                >
                  <option value="PERCENTAGE">Percentage (%)</option>
                  <option value="FIXED">Fixed amount ($)</option>
                </select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="commissionValue">Commission value</Label>
                <Input
                  id="commissionValue"
                  type="number"
                  min="0"
                  step="0.01"
                  value={localConfig.commissionValue}
                  onChange={(e) =>
                    handleFieldChange("commissionValue", e.target.value)
                  }
                  placeholder="10"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="discountType">Discount type</Label>
                <select
                  id="discountType"
                  value={localConfig.discountType}
                  onChange={(e) =>
                    handleFieldChange("discountType", e.target.value)
                  }
                  className="w-full rounded-md border border-border p-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                >
                  <option value="PERCENTAGE">Percentage (%)</option>
                  <option value="FIXED">Fixed amount ($)</option>
                </select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="discountValue">Discount value</Label>
                <Input
                  id="discountValue"
                  type="number"
                  min="0"
                  step="0.01"
                  value={localConfig.discountValue}
                  onChange={(e) =>
                    handleFieldChange("discountValue", e.target.value)
                  }
                  placeholder="10"
                />
              </div>
            </div>

            {localConfig?.commissionValue && localConfig?.discountValue ? (
              <div className="p-3 bg-muted border border-border rounded-lg text-sm text-muted-foreground">
                <p><strong>Preview:</strong> Post-checkout affiliates will earn <span className="font-semibold text-brand-600">{previewCommission}</span> commission and provide <span className="font-semibold text-brand-600">{previewDiscount}</span> discount to their referrals.</p>
              </div>
            ) : (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900">
                <p><strong>⚠️ Setup required:</strong> Please configure commission and discount values above to enable the feature.</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="space-y-6">
          <CardHeader>
            <CardTitle>Banner content</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="bannerLogo">Banner logo</Label>
                {localConfig.bannerLogo && !isUploadingLogo && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleFieldChange("bannerLogo", null)}
                    className="h-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <CloseIcon className="w-4 h-4 mr-1" />
                    Remove
                  </Button>
                )}
              </div>
              {isUploadingLogo ? (
                <div className="flex items-center justify-center py-8 border-2 border-dashed border-border rounded-lg">
                  <Loader2 className="w-6 h-6 animate-spin text-brand-600" />
                  <span className="ml-2 text-sm text-muted-foreground">Uploading...</span>
                </div>
              ) : (
                <ImageUploader
                  id="bannerLogo"
                  image={localConfig.bannerLogo ?? ""}
                  onChange={handleLogoUpload}
                  size="md"
                  shape="rectangular"
                  layout="vertical"
                  helpText="formats: PNG, JPG or WebP (max. size 3MB)"
                />
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="headlineTemplate">Headline</Label>
              <Input
                id="headlineTemplate"
                value={localConfig.headlineTemplate}
                onChange={(event) =>
                  handleFieldChange("headlineTemplate", event.target.value)
                }
                placeholder="Earn {{commissionPercentage}} cash back..."
              />
              <p className="text-sm text-muted-foreground">
                Optional: include <code>{"{{commissionPercentage}}"}</code> and
                we drop in the current commission value automatically.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="commissionMessage">Commission message</Label>
              <textarea
                id="commissionMessage"
                value={localConfig.commissionMessage}
                onChange={(event) =>
                  handleFieldChange("commissionMessage", event.target.value)
                }
                rows={3}
                className="w-full rounded-md border border-border p-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                placeholder="Share your referral link and earn cash on every purchase..."
              />
            </div>

            <div className="flex items-center justify-between gap-4">
              <div className="space-y-1">
                <Label>Include customer name</Label>
                <p className="text-sm text-muted-foreground">
                  Personalizes the headline with the buyer's first name.
                </p>
              </div>
              <Switch
                checked={localConfig.includeCustomerName}
                onCheckedChange={(checked) =>
                  handleFieldChange("includeCustomerName", Boolean(checked))
                }
              />
            </div>
          </CardContent>
        </Card>

        <div className="lg:col-span-2 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="space-y-6">
            <CardHeader>
              <CardTitle>Layout & social proof</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-3">
                <Label>Layout style</Label>
                <div className="grid grid-cols-1 gap-3">
                  {Object.entries(THANK_YOU_BANNER_LAYOUTS)
                    .filter(([layout]) => layout !== "STYLE_2")
                    .map(([layout, meta]) => (
                      <button
                        type="button"
                        key={layout}
                        onClick={() =>
                          handleFieldChange(
                            "layoutStyle",
                            layout as ThankYouBannerLayout
                          )
                        }
                        className={cn(
                          "text-left rounded-lg border p-3 transition hover:border-brand-400",
                          localConfig.layoutStyle === layout
                            ? "border-brand-500 bg-brand-50"
                            : "border-border"
                        )}
                      >
                        <p className="font-medium">{meta.label}</p>
                        <p className="text-sm text-muted-foreground">
                          {meta.description}
                        </p>
                      </button>
                    ))}
                </div>
              </div>

              <div className="space-y-3">
                <Label>Social media handles</Label>
                {data?.availableSocialMedia?.length ? (
                  <div className="space-y-2">
                    {data.availableSocialMedia.map((social) => (
                      <label
                        key={social.id}
                        className={cn(
                          "flex items-center gap-3 rounded-lg border p-3 hover:border-brand-400 transition cursor-pointer",
                          localConfig.socialMediaIds.includes(social.id)
                            ? "border-brand-500 bg-brand-50"
                            : "border-border"
                        )}
                      >
                        <Checkbox
                          checked={localConfig.socialMediaIds.includes(social.id)}
                          onCheckedChange={() => toggleSocial(social.id)}
                        />
                        <div className="flex items-center gap-3 flex-1">
                          {social.iconUrl ? (
                            <img
                              src={social.iconUrl}
                              alt={social.platformName}
                              className="w-5 h-5 object-contain"
                              onError={(e) => {
                                // Fallback to icon component if image fails to load
                                const target = e.currentTarget;
                                target.style.display = "none";
                                const iconContainer = target.parentElement?.querySelector(
                                  ".social-icon-fallback"
                                ) as HTMLElement;
                                if (iconContainer) {
                                  iconContainer.style.display = "block";
                                }
                              }}
                            />
                          ) : null}
                          <div
                            className="social-icon-fallback"
                            style={{
                              display: social.iconUrl ? "none" : "block",
                            }}
                          >
                            {getSocialIcon(social.platformName)}
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-foreground">
                              {social.platformName}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              @{social.username}
                            </p>
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Add social profiles inside Company details to showcase them
                    here.
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="border-none">
            <CardHeader>
              <CardTitle>Preview</CardTitle>
            </CardHeader>
            <CardContent className="flex justify-center">
              <div className="rounded-2xl bg-card p-6 shadow-sm border max-w-[640px] w-full">
                {isFetching && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Updating preview...
                  </div>
                )}
                {renderPreviewContent()}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Setup Instructions Modal */}
      <DialogRoot open={showSetupModal} onOpenChange={setShowSetupModal} className="max-w-2xl">
        <DialogContent>
          <DialogHeader showCloseButton onClose={() => setShowSetupModal(false)}>
            Setup Instructions
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              To display the affiliate conversion banner on your thank you page, add the extension block in your Shopify checkout settings:
            </p>
            
            <ol className="space-y-3 text-sm text-muted-foreground pl-4">
              <li className="flex gap-3">
                <span className="font-semibold text-brand-600 min-w-[20px]">1.</span>
                <span>Go to your Shopify admin and navigate to <strong>Settings → Checkout</strong></span>
              </li>
              <li className="flex gap-3">
                <span className="font-semibold text-brand-600 min-w-[20px]">2.</span>
                <span>Click the <strong>"Customize"</strong> button</span>
              </li>
              <li className="flex gap-3">
                <span className="font-semibold text-brand-600 min-w-[20px]">3.</span>
                <span>Select <strong>"Thank you"</strong> page from the dropdown at the top</span>
              </li>
              <li className="flex gap-3">
                <span className="font-semibold text-brand-600 min-w-[20px]">4.</span>
                <span>Click <strong>"Add app block"</strong> and select <strong>"Affiliate Conversion"</strong></span>
              </li>
              <li className="flex gap-3">
                <span className="font-semibold text-brand-600 min-w-[20px]">5.</span>
                <span>Position the block where you want it and click <strong>"Save"</strong></span>
              </li>
            </ol>

            <div>
              <img src={postcheckoutGuide} alt="Post-checkout setup" className="w-full h-auto" />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowSetupModal(false)}
            >
              Got it
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogRoot>
    </div>
  );
}

